import { useState, type MouseEvent } from "react";
import { Info, ShoppingCart, Store } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  formatCatalogOrderItemCount,
  formatCatalogOrderSubtotal,
  type WhatsAppCatalogOrderDisplay,
} from "@/lib/whatsapp-catalog-order";

type BubbleVariant = "outgoing" | "incoming";

interface Props {
  order: WhatsAppCatalogOrderDisplay;
  variant?: BubbleVariant;
  className?: string;
}

function OrderThumbnail({
  order,
  sizeClass = "h-14 w-14",
}: {
  order: WhatsAppCatalogOrderDisplay;
  sizeClass?: string;
}) {
  if (order.thumbnailUrl) {
    return (
      <img
        src={order.thumbnailUrl}
        alt={order.title || "Catálogo"}
        className={cn(sizeClass, "shrink-0 rounded-md object-cover")}
      />
    );
  }

  return (
    <div
      className={cn(
        sizeClass,
        "flex shrink-0 items-center justify-center rounded-md bg-muted",
      )}
    >
      <Store className="h-6 w-6 text-muted-foreground" aria-hidden />
    </div>
  );
}

export function WhatsAppCatalogOrderBubble({
  order,
  variant = "incoming",
  className,
}: Props) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isOutgoing = variant === "outgoing";
  const subtotal = formatCatalogOrderSubtotal(order.total, order.currency);
  const itemLabel = formatCatalogOrderItemCount(order.itemCount);

  const openDetails = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setDetailsOpen(true);
  };

  return (
    <>
      <div className={cn("min-w-[240px] max-w-[320px] text-sm", className)}>
        <p
          className={cn(
            "mb-2 text-xs",
            isOutgoing ? "text-primary-foreground/70" : "text-muted-foreground",
          )}
        >
          Pedido feito via catálogo
        </p>

        <div
          className={cn(
            "overflow-hidden rounded-lg border",
            isOutgoing
              ? "border-primary-foreground/15 bg-primary-foreground/5"
              : "border-border/50 bg-background/40",
          )}
        >
          <div className="flex gap-3 p-3">
            <OrderThumbnail order={order} />
            <div className="min-w-0 flex-1 self-center">
              <div className="flex items-center gap-1.5 font-semibold leading-tight">
                <ShoppingCart className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                <span>{itemLabel}</span>
              </div>
              {subtotal && (
                <p
                  className={cn(
                    "mt-1 text-xs",
                    isOutgoing ? "text-primary-foreground/75" : "text-muted-foreground",
                  )}
                >
                  Subtotal: {subtotal}
                </p>
              )}
            </div>
          </div>
        </div>

        <div
          className={cn(
            "mt-0 border-t",
            isOutgoing ? "border-primary-foreground/15" : "border-border/50",
          )}
        >
          <button
            type="button"
            onClick={openDetails}
            className={cn(
              "w-full py-2.5 text-center text-sm font-medium text-[#25D366]",
              "cursor-pointer transition-colors hover:bg-black/5 dark:hover:bg-white/5",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/40",
            )}
          >
            Mostrar pedido
          </button>
        </div>
      </div>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Detalhes do pedido</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-3">
              <OrderThumbnail order={order} sizeClass="h-16 w-16" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Pedido feito via catálogo</p>
                {order.title && (
                  <p className="mt-1 font-semibold leading-snug">{order.title}</p>
                )}
                <p className="mt-1 text-sm">{itemLabel}</p>
                {subtotal && (
                  <p className="mt-1 text-sm font-medium">Subtotal: {subtotal}</p>
                )}
              </div>
            </div>

            {order.items && order.items.length > 0 && (
              <div className="rounded-lg border border-border/60 divide-y divide-border/60">
                {order.items.map((item, idx) => {
                  const lineTotal =
                    item.price != null
                      ? formatCatalogOrderSubtotal(item.price * item.quantity, item.currency || order.currency)
                      : null;
                  const unitPrice =
                    item.price != null
                      ? formatCatalogOrderSubtotal(item.price, item.currency || order.currency)
                      : null;
                  return (
                    <div key={`${item.name}-${idx}`} className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Qtd: {item.quantity}
                          {unitPrice ? ` · ${unitPrice} un.` : ""}
                        </p>
                      </div>
                      {lineTotal && <span className="shrink-0 font-medium">{lineTotal}</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {order.orderId && (
              <p className="text-xs text-muted-foreground">
                ID do pedido: <span className="font-mono">{order.orderId}</span>
              </p>
            )}

            <Alert className="border-[#25D366]/30 bg-[#25D366]/5">
              <Info className="h-4 w-4 text-[#25D366]" aria-hidden />
              <AlertTitle className="text-sm">Continue no WhatsApp</AlertTitle>
              <AlertDescription className="text-xs text-muted-foreground">
                Pedidos de catálogo são gerenciados pelo aplicativo WhatsApp Business.
                Abra esta conversa no celular, toque em{" "}
                <span className="font-medium text-foreground">Mostrar pedido</span> na mensagem
                original e conclua o atendimento, confirmação e cobrança por lá.
              </AlertDescription>
            </Alert>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
